import { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Button } from '@antimatterhq/ui/components/button';
import { useAtomCallback } from 'jotai/utils';
import { useAtomValue } from 'jotai';
import { useDeleteDataSource } from '@/hooks/core/useDeleteDataSource';
import { useOptimisticDeleteDataSourceFromSummaryList } from '@/hooks/dbui/useGetDataSourcesSummary';
import { dataSourceToDeleteAtom } from '@/atoms/dataSources/dataSourcesAtoms';

interface DeleteDataSourceDialogProps {
  'data-testid'?: string;
}

export function DeleteDataSourceDialog(props: DeleteDataSourceDialogProps) {
  const testId = props['data-testid'] || 'delete-datasource-dialog';
  const { mutateAsync: deleteDataSource, isPending } = useDeleteDataSource();
  const deleteDataSourceFromSummaryList =
    useOptimisticDeleteDataSourceFromSummaryList();
  const dataSourceToDelete = useAtomValue(dataSourceToDeleteAtom);

  const onClose = useAtomCallback(
    useCallback((_get, set) => {
      set(dataSourceToDeleteAtom, undefined);
    }, []),
  );
  const onOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleDeleteDataSource = async () => {
    if (!dataSourceToDelete) return;

    await deleteDataSource(
      { dataSource: dataSourceToDelete },
      {
        toasts: {
          success: `${dataSourceToDelete?.name} deleted successfully`,
          error: (error) =>
            `Failed to delete ${dataSourceToDelete?.name}. "${error.response?.statusText}"`,
        },
        onSuccess: async ({ variables }) => {
          deleteDataSourceFromSummaryList(variables.dataSource);
          onClose();
        },
        onError: async ({ error }) => {
          // Not useful in this case, but ideally can be used to update form errors
          console.error(error);
        },
      },
    );
  };

  return (
    <Dialog open={dataSourceToDelete !== undefined} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-full flex-col" data-testid={testId}>
        <DialogHeader className="flex w-full flex-row items-center justify-between">
          <DialogTitle className="line-clamp-3 w-full text-ellipsis">
            Delete {dataSourceToDelete?.name || ''}?
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Are you sure you want to delete this datasource?
        </DialogDescription>
        <div className="flex w-full justify-end gap-4">
          <Button
            onClick={onClose}
            variant={'ghost'}
            disabled={isPending}
            loading={isPending}
            data-testid={`${testId}-cancel-button`}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDataSource}
            variant={'danger'}
            disabled={isPending}
            loading={isPending}
            data-testid={`${testId}-delete-button`}
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
